import { useCallback } from 'react';
import useContextualPathTransformer from '../../../../hooks/use-contextual-path-transformer';
import {
	ROUTE_SPORTSBOOK_EVENT_PAGE,
	ROUTE_SPORTSBOOK_LEAGUE_PAGE,
	ROUTE_SPORTSBOOK_LIVE_PAGE_SPORT_TAB,
	ROUTE_SPORTSBOOK_SPORT_PAGE
} from '../../../router/constants';
import { createIdWithSlug } from '../utils/sportsbook-route-utils';
import { useSearchParams, generatePath } from 'react-router-dom';
import qs from 'query-string';
import {
	TAB_EVENT_BET_BUILDER_ROUTE,
	TAB_EVENT_OPEN_BETS_ROUTE,
	TAB_LEAGUES_ROUTE,
	TAB_LIVE_ROUTE,
	TAB_OUTRIGHTS_ROUTE,
	TAB_UPCOMING_ROUTE
} from '../sportsbook-constants';

const modifySubRoute = (subRoute?: string) => {
	if (!subRoute) {
		return '';
	}

	if (subRoute.startsWith('/')) {
		return subRoute;
	}

	return `/${subRoute}`;
};

type GeneratePathParams<T extends string> = Parameters<typeof generatePath<T>>;

type SportLinkSubrouting =
	| {
			tab: typeof TAB_UPCOMING_ROUTE;
			date?: string;
	  }
	| {
			tab: typeof TAB_LIVE_ROUTE;
	  }
	| {
			tab: typeof TAB_LEAGUES_ROUTE;
			regions?: string[];
			scrollToRegion?: string;
	  }
	| {
			tab: typeof TAB_OUTRIGHTS_ROUTE;
			regions?: string[];
			scrollToRegion?: string;
	  };
export const QUERY_PARAM_DATE = 'date';
export const QUERY_PARAM_LIVE = 'live';
export const QUERY_PARAM_REGIONS = 'regions';
export const QUERY_PARAM_SCROLL_TO_REGION = 'scrollToRegion';

type EventLinkSubrouting =
	| {
			tab: typeof TAB_EVENT_OPEN_BETS_ROUTE;
	  }
	| {
			tab: typeof TAB_EVENT_BET_BUILDER_ROUTE;
	  }
	| {
			marketGroupId?: string;
	  };
export const QUERY_PARAM_MARKET_GROUP_ID = 'market-group';

/**
 * Hook for generating sportsbook routes
 */
const useSportsbookRoutes = () => {
	const transformPath = useContextualPathTransformer() as (
		path: string,
		options: { preserveContexts: boolean }
	) => string;
	const [searchParams] = useSearchParams();
	const preserveSearchParams = useCallback(
		(url: string) => {
			const query = qs.parse(searchParams.toString());
			const parsedURL = qs.parseUrl(url);

			return qs.stringifyUrl({
				url: parsedURL.url,
				query: { ...query, ...parsedURL.query },
				fragmentIdentifier: parsedURL.fragmentIdentifier
			});
		},
		[searchParams]
	);

	/**
	 * Generates a path keeping sidebar params by default
	 */
	const generateSportsbookPath = useCallback(
		<S extends string>(
			route: GeneratePathParams<S>[0],
			params: GeneratePathParams<S>[1],
			hideSidebar?: boolean
		) => {
			const path = generatePath(route, params);
			return transformPath(path, {
				preserveContexts: !hideSidebar
			});
		},
		[transformPath]
	);

	const getLivePageSportLink = useCallback(
		(sportId: string, sportName: string, subRoute?: string, hideSidebar?: boolean) => {
			const modifiedSubRoute = modifySubRoute(subRoute);
			return generateSportsbookPath(
				`${ROUTE_SPORTSBOOK_LIVE_PAGE_SPORT_TAB}${modifiedSubRoute}`,
				{
					sportIdWithSlug: createIdWithSlug(sportId, sportName)
				},
				hideSidebar
			);
		},
		[generateSportsbookPath]
	);

	const getSportPageLink = useCallback(
		(sportId: string, sportName: string, subrouting?: SportLinkSubrouting, hideSidebar?: boolean) => {
			const path = generateSportsbookPath(
				ROUTE_SPORTSBOOK_SPORT_PAGE,
				{
					sportIdWithSlug: createIdWithSlug(sportId, sportName)
				},
				hideSidebar
			);
			const parsedPath = qs.parseUrl(path);

			if (subrouting?.tab === TAB_UPCOMING_ROUTE) {
				return qs.stringifyUrl({
					...parsedPath,
					query: {
						...parsedPath.query,
						[QUERY_PARAM_DATE]: subrouting.date,
						[QUERY_PARAM_LIVE]: false
					}
				});
			}
			if (subrouting?.tab === TAB_LIVE_ROUTE) {
				return qs.stringifyUrl({
					...parsedPath,
					query: {
						...parsedPath.query,
						[QUERY_PARAM_LIVE]: true
					}
				});
			}
			if (subrouting?.tab === TAB_LEAGUES_ROUTE) {
				return qs.stringifyUrl({
					...parsedPath,
					url: `${parsedPath.url}/${TAB_LEAGUES_ROUTE}`,
					query: {
						...parsedPath.query,
						[QUERY_PARAM_REGIONS]: subrouting.regions,
						[QUERY_PARAM_SCROLL_TO_REGION]: subrouting.scrollToRegion
					}
				});
			}
			if (subrouting?.tab === TAB_OUTRIGHTS_ROUTE) {
				return qs.stringifyUrl({
					...parsedPath,
					url: `${parsedPath.url}/${TAB_OUTRIGHTS_ROUTE}`,
					query: {
						...parsedPath.query,
						[QUERY_PARAM_REGIONS]: subrouting.regions,
						[QUERY_PARAM_SCROLL_TO_REGION]: subrouting.scrollToRegion
					}
				});
			}

			return path;
		},
		[generateSportsbookPath]
	);

	const getLeaguePageLink = useCallback(
		(
			sportId: string,
			sportName: string,
			leagueId: string,
			leagueName: string,
			subRoute?: string,
			hideSidebar?: boolean
		) => {
			const modifiedSubRoute = modifySubRoute(subRoute);
			return generateSportsbookPath(
				`${ROUTE_SPORTSBOOK_LEAGUE_PAGE}${modifiedSubRoute}`,
				{
					sportIdWithSlug: createIdWithSlug(sportId, sportName),
					leagueIdWithSlug: createIdWithSlug(leagueId, leagueName)
				},
				hideSidebar
			);
		},
		[generateSportsbookPath]
	);

	const getEventPageLink = useCallback(
		(
			sportId: string,
			sportName: string,
			leagueId: string,
			leagueName: string,
			eventId: string,
			eventName: string,
			subrouting?: EventLinkSubrouting,
			hideSidebar?: boolean
		) => {
			const path = generateSportsbookPath(
				ROUTE_SPORTSBOOK_EVENT_PAGE,
				{
					sportIdWithSlug: createIdWithSlug(sportId, sportName),
					leagueIdWithSlug: createIdWithSlug(leagueId, leagueName),
					eventIdWithSlug: createIdWithSlug(eventId, eventName)
				},
				hideSidebar
			);
			const parsedPath = qs.parseUrl(path);

			if (subrouting && 'tab' in subrouting) {
				if (subrouting.tab === TAB_EVENT_OPEN_BETS_ROUTE) {
					return qs.stringifyUrl({
						...parsedPath,
						url: `${parsedPath.url}/${TAB_EVENT_OPEN_BETS_ROUTE}`,
						query: parsedPath.query
					});
				}
				if (subrouting.tab === TAB_EVENT_BET_BUILDER_ROUTE) {
					return qs.stringifyUrl({
						...parsedPath,
						url: `${parsedPath.url}/${TAB_EVENT_BET_BUILDER_ROUTE}`,
						query: parsedPath.query
					});
				}
			}

			if (subrouting?.marketGroupId) {
				return qs.stringifyUrl({
					...parsedPath,
					query: {
						...parsedPath.query,
						[QUERY_PARAM_MARKET_GROUP_ID]: subrouting.marketGroupId
					}
				});
			}

			return path;
		},
		[generateSportsbookPath]
	);

	return {
		getSportPageLink,
		getLivePageSportLink,
		getLeaguePageLink,
		getEventPageLink,
		preserveSearchParams
	};
};

export default useSportsbookRoutes;
